export function stripUtf8Bom(value: string): string {
  return value.charCodeAt(0) === 0xfeff ? value.slice(1) : value;
}

function parseFirstCsvField(line: string): string | null {
  if (line.length === 0) return null;

  // Quoted field
  if (line[0] === '"') {
    let i = 1;
    let out = '';
    while (i < line.length) {
      const ch = line[i];
      if (ch === '"') {
        // Escaped quote
        if (i + 1 < line.length && line[i + 1] === '"') {
          out += '"';
          i += 2;
          continue;
        }
        // Closing quote
        i += 1;
        return out;
      }
      out += ch;
      i += 1;
    }
    // Unclosed quote - treat as best-effort (strip leading quote)
    return out;
  }

  // Unquoted field: read until comma or end
  const commaIdx = line.indexOf(',');
  return commaIdx >= 0 ? line.slice(0, commaIdx) : line;
}

function processWhitelistLine(
  rawLine: string,
  state: { isFirstRow: boolean },
  out: Set<string>
): void {
  if (!rawLine) return;

  // Normalize CRLF when reading by '\n'
  const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
  if (!line) return;

  const firstFieldRaw = parseFirstCsvField(line);
  if (firstFieldRaw === null) return;

  const firstField = state.isFirstRow
    ? stripUtf8Bom(firstFieldRaw)
    : firstFieldRaw;

  // Skip header if present
  if (state.isFirstRow && firstField === 'filename') {
    state.isFirstRow = false;
    return;
  }

  state.isFirstRow = false;

  if (firstField.length === 0) return;
  out.add(firstField);
}

export function parseWhitelistFilenames(csvText: string): Set<string> {
  const out = new Set<string>();
  const state = { isFirstRow: true };

  const text = stripUtf8Bom(csvText);
  let start = 0;

  for (let i = 0; i <= text.length; i += 1) {
    const isEnd = i === text.length;
    const isNewline = !isEnd && text.charCodeAt(i) === 10; // '\n'
    if (!isEnd && !isNewline) continue;

    const line = text.slice(start, i);
    start = i + 1;
    processWhitelistLine(line, state, out);
  }

  return out;
}

function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError');
}

export async function parseWhitelistFilenamesFromBlob(
  blob: Blob,
  options: { signal?: AbortSignal } = {}
): Promise<Set<string>> {
  const { signal } = options;
  if (signal?.aborted) throw abortError();

  const canStream =
    typeof (blob as unknown as { stream?: unknown }).stream === 'function' &&
    typeof TextDecoderStream !== 'undefined';

  if (!canStream) {
    const text = await blob.text();
    if (signal?.aborted) throw abortError();
    return parseWhitelistFilenames(text);
  }

  const out = new Set<string>();
  const state = { isFirstRow: true };
  const stream = blob.stream().pipeThrough(new TextDecoderStream());
  const reader = stream.getReader();

  let remainder = '';
  let chunkCounter = 0;

  try {
    while (true) {
      if (signal?.aborted) throw abortError();
      const { value, done } = await reader.read();
      if (done) break;
      if (typeof value === 'string' && value.length > 0) {
        remainder += value;
      }

      let idx: number;
      while ((idx = remainder.indexOf('\n')) >= 0) {
        const line = remainder.slice(0, idx);
        remainder = remainder.slice(idx + 1);
        processWhitelistLine(line, state, out);
      }

      // Yield occasionally during large files parsing to keep UI responsive
      chunkCounter += 1;
      if (chunkCounter % 25 === 0) {
        await new Promise<void>(resolve => setTimeout(resolve, 0));
      }
    }

    if (remainder.length > 0) {
      processWhitelistLine(remainder, state, out);
    }

    return out;
  } finally {
    try {
      await reader.cancel();
    } catch {
      // Ignore cancellation errors
    }
  }
}

